<template>
    <modal title="Create a partner" size="small" name="create_partner" @modalClose="modalClose">
        <template #toggle>
            <btn type="primary">Create partner</btn>
        </template>
        <template #body>
            <div class="space-y-4 text-left">

                <alert type="error" v-if="errorMessage">{{ errorMessage }}</alert>
                <alert type="success" v-if="successMessage">{{ successMessage }}</alert>

                <div class="text-sm text-gray-500">
                    Manually create a partner, and once created, they will be added to your partner list.
                </div>

                <div>
                    <label for="email" class="block text-sm opacity-80 mb-1.5">
                        Email address
                    </label>
                    <text-field fieldId="email" @keydown.enter="createPartner"
                                autofocus="true"
                                placeholder="Enter partner's email address" :error="(formErrors.email ?? false)"
                                v-model="email"></text-field>
                </div>
                <div>
                    <label for="name" class="block text-sm opacity-80 mb-1.5">
                        Name
                    </label>
                    <text-field fieldId="name" @keydown.enter="createPartner"
                                placeholder="Enter partner's name" :error="(formErrors.name ?? false)"
                                v-model="name"></text-field>
                </div>
                <div v-show="showMore">
                    <label for="surname" class="block text-sm opacity-80 mb-1.5">
                        Last name (optional)
                    </label>
                    <text-field fieldId="surname" @keydown.enter="createPartner"
                                placeholder="Enter a last name or leave it empty" :error="(formErrors.surname ?? false)"
                                v-model="surname"></text-field>
                </div>
                <div v-show="showMore">
                    <label for="identifier" class="block text-sm opacity-80 mb-1.5">
                        Partner ID (optional)
                    </label>
                    <text-field fieldId="identifier" @keydown.enter="createPartner"
                                placeholder="Enter an ID or leave it empty" :error="(formErrors.identifier ?? false)"
                                v-model="identifier">
                        <template #note>
                            Unique ID of the partner. If not provided, ID is generated automatically.
                        </template>
                    </text-field>
                </div>
                <div v-show="showMore">
                    <label for="key" class="block text-sm opacity-80 mb-1.5">
                        Partner key / token (optional)
                    </label>
                    <text-field fieldId="key" @keydown.enter="createPartner"
                                placeholder="Enter a personalized token or leave it empty"
                                :error="(formErrors.key ?? false)"
                                v-model="key">
                        <template #note>
                            Key will be used to generate an unique referral link. If not provided, key is generated
                            automatically.
                        </template>
                    </text-field>
                </div>
                <div v-show="showMore && multiTierCommissions">
                    <label for="referring_partner_email" class="block text-sm opacity-80 mb-1.5">
                        Email address of referring partner
                    </label>
                    <text-field fieldId="referring_partner_email" @keydown.enter="createPartner"
                                placeholder="Enter referring partner email"
                                :error="(formErrors.referring_partner_email ?? false)"
                                v-model="referring_partner_email">
                        <template #note>
                            Enter referring partner' email address if this partner was referred.
                        </template>
                    </text-field>
                </div>
                <div @click.prevent="showMoreFields()" class="flex justify-end">
                    <a href="" class="flex items-center hover:underline">
                        <span class="text-xs font-medium ml-1">
                            {{ showMore ? 'Show less fields' : 'Show more fields' }}
                        </span>
                    </a>
                </div>
            </div>
        </template>
        <template #footer>
            <btn :loading="loading" @click="createPartner" type="primary" :disabled="loading">{{ loading ? 'Creating partner' : 'Create partner' }}</btn>
        </template>
    </modal>
</template>
<script>
import Icon from "../../../../elements/Icon";
import useFilters from "../../../../state/common/filters-and-pagination";

export default {
    name: 'createPartner',
    components: {Icon},
    props: ['create_url', 'multi_tier_commissions_enabled'],
    data() {
        return {
            errorMessage: false,
            successMessage: false,
            loading: false,
            formErrors: {},
            email: '',
            name: '',
            surname: '',
            identifier: '',
            key: '',
            referring_partner_email: '',
            showMore: false,
            multiTierCommissions: this.multi_tier_commissions_enabled === 1
        }
    },
    methods: {
        createPartner() {

            this.errorMessage = false;
            this.successMessage = false;

            if (this.loading || !this.valid()) {
                return;
            }

            this.loading = true;

            axios
                .post(this.create_url, {
                    email: this.email,
                    name: this.name,
                    surname: this.surname,
                    identifier: this.identifier,
                    key: this.key,
                    referring_partner_email: this.referring_partner_email,
                })
                .then(response => {
                    const status = parseInt(response.data.status || 0);
                    if (status === 1) {

                        const self = this;
                        this.successMessage = 'Partner created successfully.';

                        setTimeout(function () {
                            self.emitter.emit('modal-off', 'create_partner');
                            self.cleanForm();
                        }, 2000);

                        self.fetchPartnersData()
                    } else {
                        this.error = 'Failed to create partner';
                    }
                })
                .catch(error => {
                    let generalErrorMessage = error.response.data.error || '';
                    let emailError = error.response.data.errors?.email || '';
                    let name = error.response.data.errors?.name || '';
                    let surname = error.response.data.errors?.surname || '';
                    let identifier = error.response.data.errors?.identifier || '';
                    let key = error.response.data.errors?.key || '';

                    const errorMessage = `${generalErrorMessage} ${emailError} ${name} ${surname} ${identifier} ${key}`.trim();

                    if (errorMessage) {
                        this.errorMessage = errorMessage;
                    }
                }).finally(() => this.loading = false);
        },
        valid() {

            let valid = true;
            this.formErrors = {};

            if (this.inputIsEmpty(this.email) || /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(this.email) !== true) {
                valid = false;
                this.formErrors.email = true;
            }

            if (this.inputIsEmpty(this.name) || this.name.length > 100) {
                valid = false;
                this.formErrors.name = true;
            }

            if(this.multiTierCommissions && this.referring_partner_email.length > 1){
                if (/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(this.referring_partner_email) !== true) {
                    valid = false;
                    this.formErrors.referring_partner_email = true;
                }
            }

            return valid;
        },
        modalClose(name) {
            this.cleanForm();
        },
        cleanForm() {
            this.email = '';
            this.name = '';
            this.surname = '';
            this.identifier = '';
            this.key = '';
            this.errorMessage = false;
            this.successMessage = false;
            this.formErrors = {};
            this.showMore = false;
        },
        inputIsEmpty(value) {
            return (value ?? '').trim().length < 1;
        },
        fetchPartnersData() {
            const {
                fetchDataBaseUrl,
                fetchDataFunction,
            } = useFilters()
            fetchDataFunction.value(fetchDataBaseUrl.value)
        },
        showMoreFields() {
            this.showMore = !this.showMore;
        }
    }
}
</script>
<style scoped>
</style>
